import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import { css } from '@emotion/react';

import { buildPath, ensureDefined } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import { representativeInheritanceOf } from '@e-socna/folder-actors';
import type {
  FragmentType,
  CivilityId,
  ThirdPartyCategoryId,
  UserRoleId,
  RepresentativeTypeId,
} from '@e-socna/data-layer';

import { useFindOnePersonQuery } from '../../../pages/Person/hooks/useFindOnePersonQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { PATH_PERSON_DEFAULT } from '../../../app/constants/paths.constant';

import { LoadingCard } from '../../LoadingCard';
import { Avatar } from '../../Avatar';
import { thirdPartyTooltipOf } from '../../../utils/third-party-tooltip.util';
import { ExpandMoreCard } from '../../ExpandMoreCard';

import { ACTOR_CARD_SIZE, ACTOR_CARD_SX } from '../constants/card.constant';

import { ActorCardCmp_Representative_Fragment } from './fragments/actor-representative.fragment';
import { ActorCardCmp_Person_Fragment } from './fragments/person.fragment';
import { ActorCardCmp_OrganizationMembership_Fragment } from './fragments/organization-membership.fragment';
import { ContactInfo } from './ContactInfo';

interface Props {
  readonly consultThirdPartyInNewTab: boolean;
  readonly membership:
    | FragmentType<typeof ActorCardCmp_OrganizationMembership_Fragment>
    | undefined;
  readonly organizationName: string;
  readonly representative: FragmentType<
    typeof ActorCardCmp_Representative_Fragment
  >;
}

export const RepresentativeCard = ({
  consultThirdPartyInNewTab,
  membership: ppMembership,
  organizationName,
  representative: pRepresentative,
}: Readonly<Props>) => {
  const { pMembership } = ensureDefined({ pMembership: ppMembership });

  const {
    civilitiesIdMap,
    genericStatusesCodeMap,
    representativeTypesIdMap,
    thirdPartyCategoriesIdMap,
    userRolesIdMap,
  } = useReferencesContext();

  const representative = getFragmentData(
    ActorCardCmp_Representative_Fragment,
    pRepresentative,
  );

  const membership = getFragmentData(
    ActorCardCmp_OrganizationMembership_Fragment,
    pMembership,
  );
  const { contextualRoleIds, typeIds } = representativeInheritanceOf(
    representative,
    membership.statusId === genericStatusesCodeMap.Enabled._id
      ? membership
      : undefined,
  );

  const { data: rowPersonData, isSuccess: personIsSuccess } =
    useFindOnePersonQuery(representative.personId);

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  if (!personIsSuccess) {
    return (
      <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
        <LoadingCard />
      </Grid>
    );
  }

  const person = getFragmentData(
    ActorCardCmp_Person_Fragment,
    rowPersonData.findOnePerson,
  );

  const path = buildPath(PATH_PERSON_DEFAULT, { id: person._id });

  return (
    <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={
            <Avatar
              bgColor={person.color}
              fullName={person.fullName}
              imageUri={person.pictureImageUri}
              label={thirdPartyTooltipOf(
                person.fullName,
                typeIds
                  .map(
                    (id) =>
                      representativeTypesIdMap[id as RepresentativeTypeId]
                        ?.label ?? 'Inconnu',
                  )
                  .join(', '),
              )}
            />
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {person.fullName}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {civilitiesIdMap[person.civilityId as CivilityId]?.label ??
              'Inconnu'}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Représente
          </Typography>
          <Typography gutterBottom variant="body2">
            {organizationName}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Fonctions du Membre
          </Typography>
          <Typography gutterBottom variant="body2">
            {!membership.functions.length
              ? 'Non renseignées'
              : membership.functions.join(', ')}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Catégories du Tiers
          </Typography>
          <Typography gutterBottom variant="body2">
            {!person.categoryIds?.length
              ? 'Non renseignées'
              : person.categoryIds
                  .map(
                    (id) =>
                      thirdPartyCategoriesIdMap[id as ThirdPartyCategoryId]
                        ?.label ?? 'Inconnue',
                  )
                  .join(', ')}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Rôles Contextuels e-SOCNA
          </Typography>
          <Typography gutterBottom variant="body2">
            {!contextualRoleIds.length
              ? 'Non renseignés'
              : contextualRoleIds
                  .map(
                    (id) =>
                      userRolesIdMap[id as UserRoleId]?.label ?? 'Inconnu',
                  )
                  .join(', ')}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Types de Représentant
          </Typography>
          <Typography gutterBottom variant="body2">
            {!typeIds.length
              ? 'Non renseignés'
              : typeIds
                  .map(
                    (id) =>
                      representativeTypesIdMap[id as RepresentativeTypeId]
                        ?.label ?? 'Inconnu',
                  )
                  .join(', ')}
          </Typography>
        </CardContent>
        <CardActions disableSpacing>
          {consultThirdPartyInNewTab ? (
            <Button
              component={MuiLink}
              endIcon={<OpenInNewIcon />}
              href={path}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              variant="outlined"
            >
              Consulter
            </Button>
          ) : (
            <Button component={Link} size="medium" to={path} variant="outlined">
              Consulter
            </Button>
          )}
          <ExpandMoreCard
            expanded={expanded}
            onExpandClick={handleExpandClick}
          />
        </CardActions>
        <ContactInfo contactInfo={person.contactInfo} expanded={expanded} />
      </Card>
    </Grid>
  );
};
